import { createElement, type ChangeEvent, type ComponentProps } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { HelperText, Text as PaperText, TextInput } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import moment from 'moment';
import FieldError from 'src/presentation/components/atoms/FieldError';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import { RadioButton } from 'src/presentation/components/molecules/RadioButton';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useProfileStore } from 'src/domain/stores/useProfileStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Colors from 'src/core/utils/Colors';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import type { AccountNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Translations from 'src/core/utils/Translations';

type RenderProps = Parameters<NonNullable<ComponentProps<typeof TextInput>['render']>>[0];

type WebDateInputProps = {
	type: string;
	max: string;
	value?: string | undefined;
	ref: RenderProps['ref'];
	onFocus?: RenderProps['onFocus'] | undefined;
	onBlur?: RenderProps['onBlur'] | undefined;
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
	style: Record<string, string | number | undefined>;
};

const parseDateFieldValue = (value: string | null): Date => {
	const parsed = moment(value ?? '', Constants.dateFormatServer, true);

	return parsed.isValid() ? parsed.toDate() : new Date();
};

export default () => {
	const navigation = useNavigation<StackNavigationProp<AccountNavigation>>();
	const tabBarInset = useTabBarInset();
	const { user } = useAuthStore();
	const {
		profile: storedProfile,
		draft,
		isLoading,
		isSaving,
		validationErrors,
		saveErrorMessage,
		isDatePickerVisible,
		datePickerDate,
		getApiProfile,
		putApiPersonalData,
		setPersonalField,
		discardDraft,
		showDatePicker,
		hideDatePicker,
	} = useProfileStore();
	const { logEvent } = useServicesStore();

	const profile = draft ?? storedProfile;

	useAppStateScreenListener({
		onInit: () => getApiProfile(),
		onUnfocussed: () => discardDraft(),
	});

	const putProfileData = async () => {
		const fields = profile?.personal_data.fields;
		if (!fields) {
			return;
		}

		const extractedData: Record<string, string | null> = {};
		Object.entries(fields).forEach(([key, field]) => {
			extractedData[key] = field.value;
		});

		logEvent({
			name: Constants.analyticsEventChangeAccountSettings,
			params: {
				[Constants.analyticsParamScreenPage]: Constants.screenEditProfile,
				[Constants.analyticsParamSelectItem]: Constants.analyticsValueSelectItemPersonalData,
			},
			user,
		});

		await putApiPersonalData({
			data: extractedData,
			onSuccess: (data) => {
				if (data) {
					navigation.goBack();
				}
			},
		});
	};

	const handleConfirmDate = (date: string | Date) => {
		setPersonalField({ key: Constants.profileFieldBirthDate, value: moment(date).format(Constants.dateFormatServer) });
		hideDatePicker();
	};

	const handleChangeValue = (fieldKey: string, newValue: string) => {
		setPersonalField({ key: fieldKey, value: newValue });
	};

	const fieldErrors = (fieldKey: string): string[] | undefined => validationErrors[fieldKey];

	const profileFields = profile?.personal_data.fields;

	const WebDateInput = (props: RenderProps) => {
		return createElement<WebDateInputProps>('input', {
			type: Constants.profileFieldTypeDate,
			onBlur: props.onBlur,
			onFocus: props.onFocus,
			onChange(event) {
				props.onChangeText?.(moment(event.target.value).format(Constants.dateFormatServer));
			},
			max: moment().format(Constants.dateFormatServer),
			value: props.value,
			ref: props.ref,
			style: {
				color: Colors.inputOutline,
				fontFamily: Fonts.webInputStack,
				fontSize: Dimens.fontSize16,
				fontWeight: undefined,
				letterSpacing: Dimens.letterSpacing015,
				lineHeight: undefined,
				textAlign: 'left',
				textAlignVertical: 'center',
				background: Colors.transparent,
				padding: `${Dimens.padding24}px ${Dimens.padding16}px ${Dimens.padding4}px`,
				border: 'none',
				outline: 'none',
				height: Dimens.size30,
			},
		});
	};

	return (
		<View style={Styles.screen.white}>
			{isLoading || isSaving ? (
				<LoadingIndicator style={{ flex: Dimens.flex1 }} />
			) : (
				<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }}>
					<View style={[styles.settingBlock]}>
						<View style={Styles.screen.column}>
							<Text ellipsizeMode="head" numberOfLines={1} style={styles.sectionHeading}>
								{profile?.personal_data.label}
							</Text>
							<DateTimePickerModal
								date={datePickerDate}
								isVisible={isDatePickerVisible}
								maximumDate={new Date()}
								mode="date"
								onCancel={() => hideDatePicker()}
								onConfirm={handleConfirmDate}
							/>
							{profileFields &&
								Object.entries(profileFields).map(([key, field]) => {
									switch (field.type) {
										case Constants.profileFieldTypeDate:
											return Platform.OS === Constants.platformWeb ? (
												<View key={key}>
													<TextInput
														key={key}
														label={field.label}
														render={WebDateInput}
														style={styles.dateInput}
														value={field.value ?? ''}
														onChangeText={(e) => {
															handleChangeValue(key, e);
														}}
													/>
													<FieldError message={fieldErrors(key)} />
												</View>
											) : (
												<View key={key}>
													<TouchableOpacity onPress={() => showDatePicker(parseDateFieldValue(field.value))}>
														<TextInput
															editable={false}
															label={field.label}
															pointerEvents="none"
															style={styles.dateInput}
															value={
																field.value
																	? moment(field.value, Constants.dateFormatServer).format(Constants.dateFormatDisplayNumeric)
																	: ''
															}
														/>
													</TouchableOpacity>

													<FieldError message={fieldErrors(key)} />
												</View>
											);
										case Constants.profileFieldTypeRadio:
											return (
												<View key={key}>
													<View style={styles.radioRow}>
														{Object.entries(field.options ?? {}).map(([keyf, valuef]) => (
															<TouchableOpacity key={keyf} onPress={() => handleChangeValue(key, keyf)}>
																<View key={keyf} style={styles.radioOption}>
																	<RadioButton selected={field.value?.toLowerCase() === keyf.toLowerCase()} />
																	<PaperText style={styles.radioLabel}>{valuef}</PaperText>
																</View>
															</TouchableOpacity>
														))}
													</View>
													<FieldError message={fieldErrors(key)} />
												</View>
											);
										case Constants.profileFieldTypeNum:
											return (
												<View key={key}>
													<TextInput
														inputMode={'tel'}
														key={key}
														label={field.label}
														style={styles.textInput}
														value={field.value ?? ''}
														onChangeText={(e) => {
															handleChangeValue(key, e);
														}}
													/>
													<FieldError message={fieldErrors(key)} />
												</View>
											);
										default:
											return (
												<View key={key}>
													<TextInput
														key={key}
														label={field.label}
														style={styles.textInput}
														value={field.value ?? ''}
														onChangeText={(e) => {
															handleChangeValue(key, e);
														}}
													/>
													<FieldError message={fieldErrors(key)} />
												</View>
											);
									}
								})}
							<HelperText type="error" visible={saveErrorMessage !== null}>
								{saveErrorMessage}
							</HelperText>
							<PrimaryButton
								extraStyle={{ marginTop: Dimens.padding25 }}
								label={Translations.save}
								onPress={() => putProfileData()}
							/>
						</View>
					</View>
				</ScrollView>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	settingBlock: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: Dimens.padding24,
		marginHorizontal: Dimens.padding18,
	},
	sectionHeading: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize23,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray800,
		paddingTop: Dimens.padding10,
		paddingBottom: Dimens.padding20,
	},
	dateInput: {
		backgroundColor: Colors.gray100,
		marginBottom: Dimens.padding8,
		marginTop: Dimens.padding8,
	},
	textInput: {
		backgroundColor: Colors.gray100,
		marginTop: Dimens.padding8,
	},
	radioRow: {
		flexDirection: 'row',
	},
	radioOption: {
		flexDirection: 'row',
		padding: Dimens.padding5,
	},
	radioLabel: {
		lineHeight: Dimens.lineHeight20,
	},
});
